/*       */

import React, { PureComponent } from 'react';
import styled from 'styled-components';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { Typography, TextField } from '@mic3/platform-ui';

import ContentArea from 'app/components/molecules/PageContent/ContentArea';
import HeaderBar from 'app/components/molecules/HeaderBar/HeaderBar';
import PageTemplate from 'app/components/templates/PageTemplate';
import Loader from 'app/components/atoms/Loader/Loader';
import ButtonIcon from 'app/components/molecules/ButtonIcon/ButtonIcon';
import DotMenu from 'app/components/molecules/DotMenu/DotMenu';
import GridView from 'app/components/organisms/GridView/GridView';
import AddLlmProvider from 'app/containers/Admin/Settings/Ai/AddLlmProvider';

import { get } from 'app/utils/lo/lo';
import { bind, memoize } from 'app/utils/decorators/decoratorUtils';
import { loadLlmProviders, deleteLlmProvider } from 'store/actions/admin/llmProvidersActions';
import { setDocumentTitle } from 'store/actions/app/appActions';
import { modulesAndPageTitles } from 'app/config/typesConfig';

const StyledTypography = styled(Typography)`
    color: ${({ theme }) => theme.material.colors.text.primary};
    font-weight: 600;
`;

const Wrapper = styled.div`
    display: flex;
    flex-direction: column;
    height: 100%;
`;

const Toolbar = styled.div`
    display: flex;
    align-items: center;
    min-height: 48px;
    padding: 0 16px;
    border-bottom: 1px solid ${({ theme }) => theme.material.colors.background.divider};
`;

const GridWrap = styled.div`
    flex: 1;
    min-height: 0;
    /* Nudge the Bryntum grid toward the flat Figma look: 16px cell inset and a thin
       header divider (GridView defaults to a 2px header border). */
    & .b-grid-header-container {
        border-bottom: 1px solid ${({ theme }) => theme.material.colors.background.divider} !important;
    }
    & .b-grid-header,
    & .b-grid-cell {
        padding-left: 16px !important;
        padding-right: 16px !important;
    }
`;

// Figma renders Base URL and API Format in high-emphasis (white); GridView colours all
// cells secondary/gray by default, so force primary text for those two columns.
const PrimaryText = styled.span`
    color: ${({ theme }) => theme.material.colors.text.primary};
`;
const primaryRenderer = ({ value }) => <PrimaryText>{value || ''}</PrimaryText>;

// Mirrors the proven LeftPanelSearch pattern: standard variant, no underline, no forced
// height/padding — MUI centers the input's text naturally inside the flex-centered Toolbar.
const SearchField = styled(TextField)`
    flex-grow: 1;
    & .MuiInputBase-input {
        font-size: 14px;
        line-height: 20px;
        letter-spacing: 0.25px;
    }
    & .MuiInputBase-input::placeholder {
        color: ${({ theme }) => theme.material.colors.text.secondary};
        opacity: 1;
    }
`;
const searchInputProps = { disableUnderline: true };

const EmptyMessage = styled(Typography)`
    padding: 2rem;
    text-align: center;
    color: ${({ theme }) => theme.material.colors.text.secondary};
`;

const IconButton = styled(ButtonIcon)`
    width: 48px !important;
    height: 48px !important;
    padding: 0 !important;
    margin: 0 0 0 8px !important;
    border-radius: 50% !important;
    &:hover {
        background: ${({ theme }) => theme.material.colors.background.fields} !important;
    }
`;

const errorButtonProps = { className: 'error' };
const defaultOrderBy = { field: 'id', direction: 'asc' };

class LlmProvidersMain extends PureComponent {
    static propTypes = {
        records: PropTypes.array,
        isLoading: PropTypes.bool,
        loadLlmProviders: PropTypes.func.isRequired,
        deleteLlmProvider: PropTypes.func.isRequired,
        setDocumentTitle: PropTypes.func.isRequired,
    };

    // Actions cell — three-dot menu with Edit (opens the dialog pre-filled) and Delete
    // (confirmation names the provider; deleteLlmProvider refreshes the list on success).
    // A page-level backdrop loader (driven by isDeleting) covers the in-flight delete.
    @bind
    renderActions({ record }) {
        const row = record.data;
        const items = [
            { name: 'Edit', icon: 'pencil', onItemClick: () => this.openEdit(row) },
            {
                name: 'Delete',
                icon: 'delete',
                withConfirmation: true,
                confirmationModalProps: {
                    header: 'Delete LLM provider?',
                    message: `Are you sure you want to permanently delete '${row.id}'?`,
                    declineButtonText: 'Cancel',
                    confirmButtonText: 'Delete',
                    confirmationButtonProps: errorButtonProps,
                },
                onItemClick: () => this.props.deleteLlmProvider(row.id),
            },
        ];
        return <DotMenu tooltipTitle="Actions" items={items} />;
    }

    columns = [
        { field: 'id', text: 'Name', flex: 1, sortable: true, renderer: primaryRenderer },
        { field: 'baseUrl', text: 'Base URL', flex: 2, sortable: true, renderer: primaryRenderer },
        { field: 'apiFormat', text: 'API Format', flex: 1.5, sortable: true, renderer: primaryRenderer },
        { field: 'actions', text: '', width: 72, align: 'center', sortable: false, onClickDisabled: true, renderer: this.renderActions },
    ];

    state = {
        isAddOpen: false,
        editProvider: null,
        search: '',
    };

    componentDidMount() {
        this.props.loadLlmProviders();
        this.props.setDocumentTitle(modulesAndPageTitles.adminConsole.security.ai);
    }

    @bind
    reload() {
        this.props.loadLlmProviders();
    }

    @bind
    toggleAdd() {
        this.setState(state => ({ isAddOpen: !state.isAddOpen }));
    }

    @bind
    closeAdd() {
        this.setState({ isAddOpen: false });
    }

    @bind
    openEdit(provider) {
        this.setState({ editProvider: provider });
    }

    @bind
    closeEdit() {
        this.setState({ editProvider: null });
    }

    @bind
    onSearch(eventOrValue) {
        const value = eventOrValue && eventOrValue.target ? eventOrValue.target.value : eventOrValue;
        this.setState({ search: value || '' });
    }

    // Client-side search filter. Sorting is handled by the (readOnly) grid's own store.
    // Returns plain objects (records come from redux as frozen Immutable data, which the
    // Bryntum store must not receive).
    @bind
    @memoize()
    buildRows(records, search) {
        const term = (search || '').trim().toLowerCase();
        let rows = (records || []).filter(Boolean);
        if (term) {
            rows = rows.filter(row =>
                ['baseUrl', 'apiKey', 'id', 'apiFormat'].some(field =>
                    String(row[field] == null ? '' : row[field]).toLowerCase().includes(term)
                )
            );
        }
        return rows.map(row => ({ id: row.id, baseUrl: row.baseUrl, apiKey: row.apiKey, apiFormat: row.apiFormat }));
    }

    // The readOnly grid loads its data on mount only, so it remounts via this key. The key
    // must reflect EVERY field (not just ids) or edits to baseUrl/apiKey/apiFormat stay
    // stale. Memoized on `rows` (itself memoized) so the string isn't rebuilt each render.
    @bind
    @memoize()
    buildGridKey(rows) {
        return rows.map(r => `${r.id}|${r.baseUrl}|${r.apiKey}|${r.apiFormat}`).join('~');
    }

    render() {
        const { isLoading, records } = this.props;
        const { isAddOpen, editProvider, search } = this.state;
        const rows = this.buildRows(records, search);
        const gridKey = this.buildGridKey(rows);

        return (
            <PageTemplate title="LLM Providers" overflowHidden>
                <HeaderBar
                    rowDirection
                    left={<StyledTypography variant="h6">LLM Providers</StyledTypography>}
                    right={<IconButton icon="plus" title="Add LLM Provider" onClick={this.toggleAdd} />}
                />
                {isLoading && <Loader absolute backdrop />}
                <ContentArea withHeader>
                    <Wrapper>
                        <Toolbar>
                            <SearchField
                                value={search}
                                onChange={this.onSearch}
                                placeholder="Search..."
                                variant="standard"
                                margin="none"
                                InputProps={searchInputProps}
                                fullWidth
                            />
                            <IconButton icon="refresh" title="Refresh" onClick={this.reload} />
                        </Toolbar>
                        {rows.length > 0 ? (
                            <GridWrap>
                                {/* readOnly grid loads its data on mount only, so remount when any row value changes. */}
                                <GridView
                                    key={gridKey}
                                    id="LlmProvidersGrid"
                                    idField="id"
                                    type="llmProvider"
                                    height="100%"
                                    readOnly
                                    startIndex={0}
                                    orderBy={defaultOrderBy}
                                    columns={this.columns}
                                    list={rows}
                                    itemCount={rows.length}
                                />
                            </GridWrap>
                        ) : (
                            !isLoading && (
                                <EmptyMessage variant="body1">
                                    {search ? 'No LLM providers match your search.' : 'No LLM providers yet.'}
                                </EmptyMessage>
                            )
                        )}
                    </Wrapper>
                </ContentArea>
                {isAddOpen && <AddLlmProvider onClose={this.closeAdd} />}
                {editProvider && <AddLlmProvider provider={editProvider} onClose={this.closeEdit} />}
            </PageTemplate>
        );
    }
}

export default connect(
    state => ({
        records: get(state, 'admin.llmProviders.list.records', []),
        isLoading: get(state, 'admin.llmProviders.list.isLoading', false) || get(state, 'admin.llmProviders.update.isLoading', false),
    }),
    {
        loadLlmProviders,
        deleteLlmProvider,
        setDocumentTitle,
    }
)(LlmProvidersMain);
